import { useState } from 'react';
import { Paper, Text, Group, Badge, Stack, ActionIcon, Box } from '@mantine/core';
import { IconAlertTriangle, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import type {
  UncoveredTimeSlotInfo,
  ScheduleGridSession,
} from '../../../../services/api/cohortCoaches';

const DAY_ABBREV: Record<string, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
};

interface UncoveredTimeSlotsCalloutProps {
  uncovered: UncoveredTimeSlotInfo[];
  sessions: ScheduleGridSession[];
}

function formatTimeSlotLabel(
  dayOfWeek: string,
  displayTime: string,
  tzAbbr: string,
): string {
  const day = DAY_ABBREV[dayOfWeek] || dayOfWeek;
  return `${day} ${displayTime} ${tzAbbr}`;
}

function formatSessionHeader(sessionNumber: number, dateStr: string | null): string {
  if (!dateStr) return `Session ${sessionNumber}`;
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.toLocaleDateString('en-US', { weekday: 'short' });
  const month = d.toLocaleDateString('en-US', { month: 'short' });
  const date = d.getDate();
  return `Session ${sessionNumber} — ${day} ${month} ${date}`;
}

export function UncoveredTimeSlotsCallout({
  uncovered,
  sessions,
}: UncoveredTimeSlotsCalloutProps) {
  const [expanded, setExpanded] = useState(false);

  if (uncovered.length === 0) return null;

  // Count unique time slots (each UncoveredTimeSlotInfo is one unique time slot)
  const totalUncoveredSlots = uncovered.length;

  // Count unique sessions that have uncovered slots
  const uncoveredSessionNumbers = new Set<number>();
  for (const u of uncovered) {
    for (const s of u.sessions) {
      uncoveredSessionNumbers.add(s.session_number);
    }
  }
  const uncoveredSessionCount = uncoveredSessionNumbers.size;

  // Group uncovered by session for expanded view
  const sessionMap = new Map<number, UncoveredTimeSlotInfo[]>();
  for (const u of uncovered) {
    for (const s of u.sessions) {
      if (!sessionMap.has(s.session_number)) {
        sessionMap.set(s.session_number, []);
      }
      sessionMap.get(s.session_number)!.push(u);
    }
  }

  // Sort by session number
  const sortedSessionNumbers = [...sessionMap.keys()].sort((a, b) => a - b);

  return (
    <Paper
      p="sm"
      radius="md"
      style={{
        backgroundColor: 'rgba(233,162,59,0.08)',
        border: '1px solid rgba(233,162,59,0.3)',
      }}
    >
      <Group
        justify="space-between"
        wrap="nowrap"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <Group gap="sm" wrap="nowrap">
          <IconAlertTriangle size={18} color="#E9A23B" />
          <Text size="sm">
            <Text span fw={600}>
              {totalUncoveredSlots} time slot{totalUncoveredSlots !== 1 ? 's' : ''} with no coach availability
            </Text>
            {' '}across {uncoveredSessionCount} session{uncoveredSessionCount !== 1 ? 's' : ''}
          </Text>
        </Group>
        <ActionIcon variant="subtle" color="dark" size="sm">
          {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
        </ActionIcon>
      </Group>

      {expanded && (
        <Stack gap="md" mt="sm" pl={30}>
          {sortedSessionNumbers.map((sn) => {
            const sessionInfo = sessions.find(s => s.session_number === sn);
            const slots = sessionMap.get(sn) || [];
            return (
              <Box key={sn}>
                <Text size="xs" fw={600} c="#1A1A1A" mb={6}>
                  {formatSessionHeader(sn, sessionInfo?.scheduled_date ?? null)}
                </Text>
                <Group gap={6} wrap="wrap">
                  {slots.map((u) => (
                    <Badge
                      key={u.time_slot.id}
                      variant="outline"
                      color="warningAmber"
                      size="sm"
                      radius="sm"
                      style={{ fontWeight: 400, textTransform: 'none' }}
                    >
                      {formatTimeSlotLabel(
                        u.time_slot.day_of_week,
                        u.time_slot.display_time,
                        u.time_slot.timezone_abbreviation,
                      )}
                    </Badge>
                  ))}
                </Group>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}
